import { Chart } from 'chart.js';

const fmtEuro = (cents, decimals = 0) =>
  '€' + (cents / 100).toLocaleString('nl-BE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const PALETTE = {
  mrr: '#2563eb',       // blue-600
  new: '#10b981',       // emerald-500
  expansion: '#5eead4', // teal-300
  contraction: '#f59e0b', // amber-500
  churn: '#ef4444',     // red-500
  net: '#6366f1',       // indigo-500
  plan: ['#2563eb', '#7c3aed', '#0ea5e9', '#10b981', '#f59e0b', '#ec4899', '#14b8a6', '#f43f5e'],
};

/**
 * Alpine-component voor de master "Groei"-pagina. Rendert 5 Chart.js-grafieken
 * en herlaadt trend + movement bij een periode-wissel via /master/groei/data.
 * Dark-mode-bewust: her-thema't alle grafieken zodra de 'dark'-class wijzigt.
 */
export function groeiCharts(config) {
  return {
    range: config.range || '12m',
    vertical: config.vertical || '',
    dataUrl: config.dataUrl || '/master/groei/data',
    loading: false,
    data: {
      trend: config.trend || [],
      movement: config.movement || [],
      breakdowns: config.breakdowns || { by_plan: [], by_vertical: [] },
    },
    charts: {},
    _observer: null,

    init() {
      this.renderAll();
      // Her-thema bij dark-mode toggle (Tailwind 'dark'-class op <html>).
      this._observer = new MutationObserver(() => this.retheme());
      this._observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
      this.$watch('range', () => this.reload());
    },

    destroy() {
      this._observer?.disconnect();
      Object.values(this.charts).forEach((c) => c?.destroy());
    },

    theme() {
      const dark = document.documentElement.classList.contains('dark');
      return {
        text: dark ? '#9ca3af' : '#6b7280',
        grid: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
        tooltipBg: dark ? '#1f2937' : '#111827',
      };
    },

    baseOptions(extra = {}) {
      const t = this.theme();
      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { labels: { color: t.text, boxWidth: 12, usePointStyle: true } },
          tooltip: { backgroundColor: t.tooltipBg, padding: 10, cornerRadius: 6 },
        },
        scales: {
          x: { ticks: { color: t.text }, grid: { display: false } },
          y: {
            ticks: { color: t.text, callback: (v) => fmtEuro(v) },
            grid: { color: t.grid },
          },
        },
        ...extra,
      };
    },

    renderAll() {
      this.buildTrend();
      this.buildMovement();
      this.buildTenants();
      this.buildPlan();
      this.buildVertical();
    },

    retheme() {
      // Goedkoopste betrouwbare her-thema: alles opnieuw opbouwen.
      Object.values(this.charts).forEach((c) => c?.destroy());
      this.charts = {};
      this.renderAll();
    },

    // 1. MRR-trend (lijn)
    buildTrend() {
      const t = this.theme();
      const d = this.data.trend;
      this.charts.trend = new Chart(this.$refs.trend, {
        type: 'line',
        data: {
          labels: d.map((r) => r.label),
          datasets: [{
            label: 'MRR',
            data: d.map((r) => r.mrr_cents),
            borderColor: PALETTE.mrr,
            backgroundColor: 'rgba(37,99,235,0.10)',
            fill: true,
            tension: 0.3,
            pointRadius: d.map((r) => (r.source === 'backfill' ? 2 : 3)),
            pointStyle: d.map((r) => (r.source === 'backfill' ? 'rectRot' : 'circle')),
            pointBackgroundColor: d.map((r) => (r.source === 'backfill' ? '#9ca3af' : PALETTE.mrr)),
          }],
        },
        options: this.baseOptions({
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: t.tooltipBg, padding: 10, cornerRadius: 6,
              callbacks: {
                label: (ctx) => 'MRR ' + fmtEuro(ctx.raw, 2)
                  + (this.data.trend[ctx.dataIndex]?.source === 'backfill' ? ' (gereconstrueerd)' : ''),
                afterLabel: (ctx) => 'ARR ' + fmtEuro(ctx.raw * 12),
              },
            },
          },
        }),
      });
    },

    // 2. MRR-movement (gestapelde staaf + netto-lijn)
    buildMovement() {
      const t = this.theme();
      const d = this.data.movement;
      this.charts.movement = new Chart(this.$refs.movement, {
        data: {
          labels: d.map((r) => r.label),
          datasets: [
            { type: 'bar', label: 'New', data: d.map((r) => r.new_cents), backgroundColor: PALETTE.new, stack: 's' },
            { type: 'bar', label: 'Expansion', data: d.map((r) => r.expansion_cents), backgroundColor: PALETTE.expansion, stack: 's' },
            { type: 'bar', label: 'Contraction', data: d.map((r) => r.contraction_cents), backgroundColor: PALETTE.contraction, stack: 's' },
            { type: 'bar', label: 'Churn', data: d.map((r) => r.churn_cents), backgroundColor: PALETTE.churn, stack: 's' },
            { type: 'line', label: 'Netto', data: d.map((r) => r.net_cents), borderColor: PALETTE.net, backgroundColor: PALETTE.net, tension: 0.3, pointRadius: 3 },
          ],
        },
        options: this.baseOptions({
          plugins: {
            legend: { labels: { color: t.text, boxWidth: 12, usePointStyle: true } },
            tooltip: {
              backgroundColor: t.tooltipBg, padding: 10, cornerRadius: 6,
              callbacks: { label: (ctx) => ctx.dataset.label + ' ' + fmtEuro(ctx.raw, 2) },
            },
          },
          scales: {
            x: { stacked: true, ticks: { color: t.text }, grid: { display: false } },
            y: { stacked: true, ticks: { color: t.text, callback: (v) => fmtEuro(v) }, grid: { color: t.grid } },
          },
        }),
      });
    },

    // 3. Tenants new vs churned (staaf)
    buildTenants() {
      const t = this.theme();
      const d = this.data.movement;
      this.charts.tenants = new Chart(this.$refs.tenants, {
        type: 'bar',
        data: {
          labels: d.map((r) => r.label),
          datasets: [
            { label: 'Nieuw', data: d.map((r) => r.new_tenants), backgroundColor: PALETTE.new },
            { label: 'Verloren', data: d.map((r) => -r.churned_tenants), backgroundColor: PALETTE.churn },
          ],
        },
        options: this.baseOptions({
          scales: {
            x: { ticks: { color: t.text }, grid: { display: false } },
            y: { ticks: { color: t.text, precision: 0, callback: (v) => Math.abs(v) }, grid: { color: t.grid } },
          },
          plugins: {
            legend: { labels: { color: t.text, boxWidth: 12, usePointStyle: true } },
            tooltip: {
              backgroundColor: t.tooltipBg, padding: 10, cornerRadius: 6,
              callbacks: { label: (ctx) => ctx.dataset.label + ': ' + Math.abs(ctx.raw) },
            },
          },
        }),
      });
    },

    // 4. Breakdown per plan (horizontale staaf)
    buildPlan() {
      const t = this.theme();
      const d = this.data.breakdowns.by_plan || [];
      this.charts.plan = new Chart(this.$refs.plan, {
        type: 'bar',
        data: {
          labels: d.map((r) => r.name),
          datasets: [{
            label: 'MRR',
            data: d.map((r) => r.mrr_cents),
            backgroundColor: d.map((_, i) => PALETTE.plan[i % PALETTE.plan.length]),
            borderRadius: 4,
          }],
        },
        options: this.baseOptions({
          indexAxis: 'y',
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: t.tooltipBg, padding: 10, cornerRadius: 6,
              callbacks: {
                label: (ctx) => fmtEuro(ctx.raw, 2) + ' · ' + (d[ctx.dataIndex]?.count ?? 0) + ' tenants',
              },
            },
          },
          scales: {
            x: { ticks: { color: t.text, callback: (v) => fmtEuro(v) }, grid: { color: t.grid } },
            y: { ticks: { color: t.text }, grid: { display: false } },
          },
        }),
      });
    },

    // 5. Breakdown per vertical (doughnut)
    buildVertical() {
      const t = this.theme();
      const d = this.data.breakdowns.by_vertical || [];
      this.charts.vertical = new Chart(this.$refs.vertical, {
        type: 'doughnut',
        data: {
          labels: d.map((r) => r.name),
          datasets: [{
            data: d.map((r) => r.mrr_cents),
            backgroundColor: d.map((_, i) => PALETTE.plan[i % PALETTE.plan.length]),
            borderWidth: 0,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom', labels: { color: t.text, boxWidth: 12, usePointStyle: true } },
            tooltip: {
              backgroundColor: t.tooltipBg, padding: 10, cornerRadius: 6,
              callbacks: {
                label: (ctx) => ctx.label + ': ' + fmtEuro(ctx.raw, 2)
                  + ' · ' + (d[ctx.dataIndex]?.count ?? 0) + ' tenants',
              },
            },
          },
        },
      });
    },

    async reload() {
      this.loading = true;
      try {
        const verticalQs = this.vertical ? `&vertical=${encodeURIComponent(this.vertical)}` : '';
        const res = await fetch(`${this.dataUrl}?range=${encodeURIComponent(this.range)}${verticalQs}`, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!res.ok) return;
        const json = await res.json();
        if (!json.ok) return;
        this.data.trend = json.trend;
        this.data.movement = json.movement;
        // Alleen de periode-afhankelijke grafieken herbouwen.
        this.charts.trend?.destroy();
        this.charts.movement?.destroy();
        this.charts.tenants?.destroy();
        this.buildTrend();
        this.buildMovement();
        this.buildTenants();
      } catch (e) {
        // stil — pagina blijft staan met de vorige data
      } finally {
        this.loading = false;
      }
    },
  };
}
